import {isNil} from 'ramda';

import type {
  FeatureResponseBody,
  PackageResponseBody,
  PackageWriteRequestBody,
  PricedItemBatchOperation,
} from '@dms/api/new-vehicle';

import type {PricedItemBatchEntry} from '../types/PricedItemBatchEntry';
import type {PricedItemChange} from '../types/PricedItemChange';
import {featureTaxonomyUpdates} from './featureTaxonomyUpdates';
import {pricedItemGroupKeys} from './pricedItemGroupKeys';
import {unchangedStoredVersion} from './unchangedStoredVersion';

type TaxonomyValues = {memberFeatureIds: string[]; packageOnlyFeatureIds: string[]};

/**
 * The `feature.update` operations of the members whose "orderable alone" the save changes, from the
 * features read again at build: the writes of the single save (featureTaxonomyUpdates) with their
 * current versions, and none for a member that has the wanted value meanwhile (Q8).
 */
const memberUpdates = async (
  changedFeatureIds: string[],
  taxonomy: TaxonomyValues,
  readFeature: (featureId: string) => Promise<FeatureResponseBody>
) => {
  const features = await Promise.all(changedFeatureIds.map(readFeature));
  return featureTaxonomyUpdates(features, taxonomy).map(
    ({featureId, featureWriteRequestBody}): PricedItemBatchOperation => ({
      type: 'feature.update',
      targetId: featureId,
      body: featureWriteRequestBody,
    })
  );
};

/**
 * The entry of a package create or update for an open group. It sends the package write, then the
 * `feature.update` of each member whose "orderable alone" the single save changes, and no other
 * library write (Q8). An update sends its body only while the package has the version the body was
 * made from (unchangedStoredVersion); a rename uses the new name and frees the stored one. A trim
 * that the group creates can be offered (`referencedIds`): removing its create removes this entry.
 */
export const packageWriteEntry = (options: {
  body: PackageWriteRequestBody;
  items: PricedItemChange[];
  stored: Pick<PackageResponseBody, 'id' | 'version' | 'name'> | undefined;
  readVersion: (packageId: string) => Promise<number>;
  taxonomy: TaxonomyValues;
  /** The members whose "orderable alone" the save changes, as the builder read them. */
  changedFeatureIds: string[];
  readFeature: (featureId: string) => Promise<FeatureResponseBody>;
  /** The trims of the year that a create of the group makes. */
  groupedTrimIds: string[];
}): Omit<PricedItemBatchEntry, 'items'> => {
  const {body, items, stored, changedFeatureIds} = options;
  const trimIds = body.trims.map(({trimId}) => trimId);
  const shared = {
    title: body.name,
    extraKeys: changedFeatureIds.map(pricedItemGroupKeys.feature),
    operationCount: 1 + changedFeatureIds.length,
    referencedIds: trimIds.filter((trimId) => options.groupedTrimIds.includes(trimId)),
  };
  const withMemberUpdates = async (packageOperation: Promise<PricedItemBatchOperation>) => {
    const [operation, updates] = await Promise.all([
      packageOperation,
      memberUpdates(changedFeatureIds, options.taxonomy, options.readFeature),
    ]);
    return {items, operations: [operation, ...updates]};
  };
  if (isNil(stored)) {
    const grouped = pricedItemGroupKeys.newCreate('package');
    return {
      ...grouped,
      ...shared,
      usedValues: pricedItemGroupKeys.usedValue('package', 'name', body.name),
      newItemTrimIds: trimIds,
      build: () =>
        withMemberUpdates(
          Promise.resolve<PricedItemBatchOperation>({
            type: 'package.create',
            id: grouped.createdId,
            body,
          })
        ),
    };
  }
  return {
    ...shared,
    key: pricedItemGroupKeys.package(stored.id),
    usedValues:
      body.name === stored.name
        ? []
        : [
            ...pricedItemGroupKeys.usedValue('package', 'name', body.name),
            ...pricedItemGroupKeys.usedValue('package', 'name', stored.name),
          ],
    build: () =>
      withMemberUpdates(
        unchangedStoredVersion(stored.version, () => options.readVersion(stored.id)).then(
          (version): PricedItemBatchOperation => ({
            type: 'package.update',
            targetId: stored.id,
            body: {...body, version},
          })
        )
      ),
  };
};
